import { setHash } from './hashRouting';
import { playNotificationSoundWithUserInteraction } from './audio';

export class ChromeNotification {
  private static instance: ChromeNotification;
  private permission: NotificationPermission = 'default';

  private constructor() {
    this.checkPermission();
  }

  static getInstance(): ChromeNotification {
    if (!ChromeNotification.instance) {
      ChromeNotification.instance = new ChromeNotification();
    }
    return ChromeNotification.instance;
  }

  /**
   * 알림 권한 확인
   */
  private checkPermission(): void {
    if ('Notification' in window) {
      this.permission = Notification.permission;
    }
  }

  /**
   * 알림 권한 요청
   */
  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('이 브라우저는 알림을 지원하지 않습니다.');
      return false;
    }

    if (this.permission === 'granted') {
      return true;
    }

    if (this.permission === 'denied') {
      console.warn('알림 권한이 거부되었습니다. 브라우저 설정에서 권한을 허용해주세요.');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    } catch (error) {
      console.error('알림 권한 요청 실패:', error);
      return false;
    }
  }

  /**
   * 알림 표시
   * @param title 알림 제목
   * @param img 알림 아이콘 이미지 URL
   * @param options 알림 옵션
   * @param onClick 알림 클릭 시 실행할 콜백 함수 (선택적)
   */
  showNotification(
    title: string, 
    img: string, 
    options?: NotificationOptions,
    onClick?: () => void
  ): boolean {
    if (!('Notification' in window) || this.permission !== 'granted') {
      return false;
    }

    try {
      const notification = new Notification(title, {
        icon: img || 'https://metadata.pumplify.eu/data/QmW3xobmXDYAnUooXbRWE3a8DvHMuJ7qRx7BLgs1Eu1QpX',
        badge: img || 'https://metadata.pumplify.eu/data/QmW3xobmXDYAnUooXbRWE3a8DvHMuJ7qRx7BLgs1Eu1QpX',
        tag: 'af-monitor-notification',
        requireInteraction: false,
        silent: false,
        ...options
      });

      // 알림 클릭 시 처리
      notification.onclick = () => {
        window.focus();
        // 커스텀 클릭 핸들러가 있으면 실행
        if (onClick) {
          onClick();
        }
        notification.close();
      };

      playNotificationSoundWithUserInteraction();
      setTimeout(() => {
        notification.close();
      }, 30000); // 30초

      return true;
    } catch (error) {
      console.error('알림 표시 실패:', error);
      return false;
    }
  }

  /**
   * 랭킹 변화 알림
   */
  showRankChangeNotification(
    tokenSymbol: string, 
    img: string,
    rankType: '누적' | '피크', 
    oldRank: number, 
    newRank: number,
    threshold: number,
    tokenAddress: string
  ): boolean {
    const title = `🚀 $${tokenSymbol} ${rankType} 랭킹 변화`;
    const body = `${rankType} 랭킹이 ${threshold}% 이상 변화했습니다.`;
    
    return this.showNotification(
      title, 
      img, 
      {
        body,
        tag: `rank-change-${tokenSymbol}`,
        requireInteraction: true
      },
      () => {
        // 알림 클릭 시 토큰 모달로 이동
        setHash(tokenAddress, 'token');
      }
    );
  }

  /**
   * Legendary Address 구매 알림
   */
  showLegendaryInflowNotification(
    tokenSymbol: string, 
    img: string, 
    legendaryAddress: string,
    tokenAddress: string
  ): boolean {
    const title = `⭐ Legendary 구매 감지!`;
    const shortAddress = legendaryAddress.slice(0, 4);
    const body = `${shortAddress}이(가) $${tokenSymbol}을 구매했습니다!`;
    
    return this.showNotification(
      title, 
      img, 
      {
        body,
        tag: `legendary-inflow-${tokenSymbol}`,
        requireInteraction: true
      },
      () => {
        // 알림 클릭 시 토큰 모달로 이동
        setHash(tokenAddress, 'token');
      }
    );
  }

  /**
   * Legendary Address 수 알림
   */
  showLegendaryCountNotification(
    tokenSymbol: string, 
    img: string, 
    legendaryCount: number,
    tokenAddress: string
  ): boolean {
    const title = `👑 Legendary 집중!`;
    const body = `${legendaryCount}명의 Legendary Address가 $${tokenSymbol}을 보유하고 있습니다!`;
    
    return this.showNotification(
      title, 
      img, 
      {
        body,
        tag: `legendary-count-${tokenSymbol}`,
        requireInteraction: true
      },
      () => {
        // 알림 클릭 시 토큰 모달로 이동
        setHash(tokenAddress, 'token');
      }
    );
  }

  /**
   * 랭킹 상위 위치 알림
   */
  showRankHighPositionNotification(
    tokenSymbol: string, 
    img: string, 
    rank: number,
    tokenAddress: string
  ): boolean {
    const title = `🚀 랭킹 급상승!`;
    const body = `$${tokenSymbol}이(가) 빠르게 ${rank}%에 도달했습니다!`;
    
    return this.showNotification(
      title, 
      img, 
      {
        body,
        tag: `rank-high-${tokenSymbol}`,
        requireInteraction: true
      },
      () => {
        // 알림 클릭 시 토큰 모달로 이동
        setHash(tokenAddress, 'token');
      }
    );
  }

  /**
   * 거래량 높은 활동 알림
   */
  showVolumeHighActivityNotification(
    tokenSymbol: string, 
    img: string, 
    rank: number,
    tokenAddress: string
  ): boolean {
    const title = `📈 거래량 폭증!`;
    const body = `$${tokenSymbol}이(가) 높은 거래량으로 ${rank}%에 도달했습니다!`;
    
    return this.showNotification(
      title, 
      img, 
      {
        body,
        tag: `volume-high-${tokenSymbol}`,
        requireInteraction: true
      },
      () => {
        // 알림 클릭 시 토큰 모달로 이동
        setHash(tokenAddress, 'token');
      }
    );
  }

  /**
   * Surge Alert 알림
   */
  showSurgeAlertNotification(
    tokenSymbol: string, 
    img: string, 
    rankType: '누적' | '피크',
    tokenAddress: string
  ): boolean {
    const title = `⚡ $${tokenSymbol} 활성 알림!`;
    const body = `${rankType} 랭크가 30분 동안 1%를 유지하고 있습니다!`;
    
    return this.showNotification(
      title, 
      img, 
      {
        body,
        tag: `surge-alert-${tokenSymbol}-${rankType}`,
        requireInteraction: true
      },
      () => {
        // 알림 클릭 시 토큰 모달로 이동
        setHash(tokenAddress, 'token');
      }
    );
  }

  /**
   * 권한 상태 확인
   */
  getPermissionStatus(): NotificationPermission {
    return this.permission;
  }

  /**
   * 알림 지원 여부 확인
   */
  isSupported(): boolean {
    return 'Notification' in window;
  }
}

// 싱글톤 인스턴스 export
export const chromeNotification = ChromeNotification.getInstance();
